import {Editor, Element, Node, Path, Transforms} from "slate";

import {
    paragraph,
    table,
    tableBody,
    tableCell,
    tableRow,
    text,
} from "../node-builders.ts";

import {isElementOfType} from "./node-helpers.ts";

/**
 * Returns true if the current selection is somewhere within a table
 * element, and false otherwise.
 */
export const isSelectionWithinTable = (editor: Editor) => {
    const [table] = Editor.nodes(editor, {
        match: (n) => Element.isElement(n) && n.type === "table",
    });
    return !!table;
};

/**
 * Returns true if the given path is within a table-header element,
 * and false otherwise.
 */
export const isWithinTableHeader = (editor: Editor, path: Path) => {
    const [thead] = Editor.nodes(editor, {
        match: (n) => Element.isElement(n) && n.type === "table-header",
        at: path,
    });
    return !!thead;
};

/**
 * If not already within a table, adds a new empty 2x2 table at the cursor
 * position.
 */
export const addEmptyTable = (editor: Editor) => {
    const [parentTable] = Editor.nodes(editor, {
        match: (n: Node) => isElementOfType(n, "table"),
        at: editor.selection!,
    });
    if (parentTable) {
        // We already have a parent table; don't do anything!
        return;
    }

    Transforms.insertNodes(
        editor,
        table(
            tableBody(
                tableRow(tableCell(text("")), tableCell(text(""))),
                tableRow(tableCell(text("")), tableCell(text(""))),
            ),
        ),
    );
};

/**
 * Adds a new column after the column of the current table-cell at the
 * selection. If the selection is not within a table or table-cell, does
 * nothing.
 */
export const addColumnAfter = (editor: Editor) => {
    const [table, cell] = Editor.nodes(editor, {
        match: (n: Node) => isElementOfType(n, "table", "table-cell"),
        at: editor.selection!,
    });
    if (!table || !cell) {
        // No parent table or cell; can't do anything!
        return;
    }

    const [, startingCellPath] = cell;

    // Figure out the index to the new column (the next one after the
    // starting column):
    const nextColumn = Path.next(startingCellPath);
    const columnIndexToInsert = nextColumn.at(-1)!;

    Editor.withoutNormalizing(editor, () => {
        const [, tablePath] = table;

        // For each section (table-header, table-body)...
        for (const [, sectionPath] of Node.children(editor, tablePath)) {
            // For each table-row within that section...
            for (const [, rowPath] of Node.children(editor, sectionPath)) {
                // Insert a new empty table-cell at column within that row:
                Transforms.insertNodes(editor, tableCell(text("")), {
                    at: [...rowPath, columnIndexToInsert],
                });
            }
        }
    });
};

/**
 * Adds a new table-row after the table-row containing the current selection.
 * If the selection is not within a table-row, does nothing.
 */
export const addRowAfter = (editor: Editor) => {
    const [section, row] = Editor.nodes(editor, {
        match: (n: Node) =>
            isElementOfType(n, "table-header", "table-body", "table-row"),
        at: editor.selection!,
    });
    if (!section || !row) {
        // No parent table section or row; can't do anything!
        return;
    }

    // Figure out the index to the new row:
    const [, rowPath] = row;
    const rowIndexToInsert = Path.next(rowPath).at(-1)!;

    // We count up how many cells we'll need to insert into this new row
    // (the number of children of the starting row path):
    const cellCount = countNodesAtPath(editor, rowPath);
    if (cellCount < 1) {
        return;
    }

    Editor.withoutNormalizing(editor, () => {
        const [, sectionPath] = section;

        const newCells = Array.from({length: cellCount}).map(() =>
            tableCell(text("")),
        );

        Transforms.insertNodes(editor, tableRow(...newCells), {
            at: [...sectionPath, rowIndexToInsert],
        });
    });
};

/**
 * Removes all table-cells in the column of the current selection.
 * If the selection is not within a table or table-cell, does nothing.
 */
export const deleteColumn = (editor: Editor) => {
    const [table, cell] = Editor.nodes(editor, {
        match: (n: Node) => isElementOfType(n, "table", "table-cell"),
        at: editor.selection!,
    });
    if (!table || !cell) {
        // No parent table or cell; can't do anything!
        return;
    }

    // Figure out the index to column we're removing:
    const [, startingCellPath] = cell;
    const columnIndexToRemove = startingCellPath.at(-1)!;

    Editor.withoutNormalizing(editor, () => {
        const [, tablePath] = table;

        // For each section (table-header, table-body)...
        for (const [, sectionPath] of Node.children(editor, tablePath)) {
            // For each table-row within that section...
            for (const [, rowPath] of Node.children(editor, sectionPath)) {
                // Remove the cell at that index within the row:
                Transforms.removeNodes(editor, {
                    at: [...rowPath, columnIndexToRemove],
                });
            }
        }
    });
};

/**
 * Deletes the table-row containing the current selection. If the selection
 * is not within a table-row, does nothing.
 */
export const deleteRow = (editor: Editor) => {
    const [section, row] = Editor.nodes(editor, {
        match: (n: Node) =>
            isElementOfType(n, "table-header", "table-body", "table-row"),
        at: editor.selection!,
    });
    if (!section || !row) {
        // No parent table section or row; can't do anything!
        return;
    }

    // Figure out the index to row we're removing:
    const [, rowPath] = row;
    const rowIndexToRemove = rowPath.at(-1)!;

    Editor.withoutNormalizing(editor, () => {
        const [, sectionPath] = section;

        // Remove the row at that index within the section:
        Transforms.removeNodes(editor, {
            at: [...sectionPath, rowIndexToRemove],
        });
    });
};

/**
 * Deletes the entire table containing the current selection. If the selection
 * is not within a table, does nothing.
 */
export const deleteTable = (editor: Editor) => {
    const [table] = Editor.nodes(editor, {
        match: (n: Node) => isElementOfType(n, "table"),
        at: editor.selection!,
    });
    if (!table) {
        // No parent table; can't do anything!
        return;
    }

    const [, tablePath] = table;

    // Remove the table element by its path:
    Transforms.removeNodes(editor, {
        at: tablePath,
    });
};

/**
 * Adds a new paragraph after the table at the current selection, and moves
 * the selection to the new paragraph. If the current selection isn't within
 * a table, does nothing.
 */
export const newParagraphAfterTable = (editor: Editor) => {
    const [table] = Editor.nodes(editor, {
        match: (n: Node) => isElementOfType(n, "table"),
        at: editor.selection!,
    });
    if (!table) {
        // No parent table; bail out!
        return;
    }

    const [, tablePath] = table;
    const newParagraphPath = Path.next(tablePath);

    Transforms.insertNodes(editor, paragraph(text("")), {at: newParagraphPath});
    Transforms.select(editor, newParagraphPath);
};

/**
 * When selection is within a table-cell, moves the selection to the following
 * table-cell. This means the cell to the right in the same row, if there is
 * one, or it wraps around to the first cell in the row below. If in the last
 * cell of a table, moves selection out of the table.
 *
 * If selection is not within a table-cell, does nothing.
 */
export const moveSelectionToNextCell = (editor: Editor) => {
    const [table, section, row, cell] = Editor.nodes(editor, {
        match: (n: Node) =>
            isElementOfType(
                n,
                "table",
                "table-body",
                "table-header",
                "table-row",
                "table-cell",
            ),
        at: editor.selection!,
    });
    if (!cell) {
        // No parent cell; bail out.
        return;
    }

    // We first look for a cell to the right to select.
    const [, cellPath] = cell;
    const cellPathToRight = Path.next(cellPath);
    if (Editor.hasPath(editor, cellPathToRight)) {
        // There is a cell to the right! Move there.
        Transforms.select(editor, cellPathToRight);
        return;
    }

    // Next, we look for a row below to move to within the same section:
    const [, rowPath] = row;
    const rowPathBelow = Path.next(rowPath);
    if (Editor.hasPath(editor, rowPathBelow)) {
        // There is a row below! Move to the first cell there.
        Transforms.select(editor, [...rowPathBelow, 0]);
        return;
    }

    // Next, we look for a section below (e.g. if we're moving out of a
    // table-header and into a table-body).
    const [, sectionPath] = section;
    const sectionPathBelow = Path.next(sectionPath);
    if (Editor.hasPath(editor, sectionPathBelow)) {
        if (Editor.hasPath(editor, [...sectionPathBelow, 0])) {
            // Found one! Select the first row, first cell, in this section:
            Transforms.select(editor, [...sectionPathBelow, 0, 0]);
        }
        return;
    }

    // We failed to find a cell, row, or section to move to. We now move
    // out of the table to the next element in the editor, if we can...
    const [, tablePath] = table;
    const pathAfterTable = Path.next(tablePath);
    if (Editor.hasPath(editor, pathAfterTable)) {
        Transforms.select(editor, Editor.start(editor, pathAfterTable));
    } else {
        // And if not, we make a new empty paragraph and move there!
        newParagraphAfterTable(editor);
    }
};

/**
 * When selection is within a table-cell, moves the selection to the previous
 * table-cell. This means the cell to the left in the same row, if there is
 * one, or it wraps around to the last cell in the row above. If in the first
 * cell of a table, moves selection out of the table to the preceding element.
 *
 * If selection is not within a table-cell, does nothing.
 */
export const moveSelectionToPreviousCell = (editor: Editor) => {
    const [table, section, row, cell] = Editor.nodes(editor, {
        match: (n: Node) =>
            isElementOfType(
                n,
                "table",
                "table-body",
                "table-header",
                "table-row",
                "table-cell",
            ),
        at: editor.selection!,
    });
    if (!cell) {
        // No parent cell; bail out.
        return;
    }

    // First, we look for a cell to the left of this one to select:
    const [, cellPath] = cell;
    if (Path.hasPrevious(cellPath)) {
        const cellPathToLeft = Path.previous(cellPath);
        if (Editor.hasPath(editor, cellPathToLeft)) {
            // There is a cell to the left! Move there.
            Transforms.select(editor, cellPathToLeft);
            return;
        }
    }

    // Next, we look for a row above this one:
    const [, rowPath] = row;
    if (Path.hasPrevious(rowPath)) {
        const rowPathAbove = Path.previous(rowPath);
        if (Editor.hasPath(editor, rowPathAbove)) {
            // There is a row above! Move to the last cell there.
            const cellCount = countNodesAtPath(editor, rowPathAbove);
            Transforms.select(editor, [...rowPathAbove, cellCount - 1]);
            return;
        }
    }

    // Next, we look for a section above this one (if we're moving
    // out of a table-body and into a table-header, for example):
    const [, sectionPath] = section;
    if (Path.hasPrevious(sectionPath)) {
        const sectionPathAbove = Path.previous(sectionPath);
        if (Editor.hasPath(editor, sectionPathAbove)) {
            const rowCount = countNodesAtPath(editor, sectionPathAbove);
            const rowPathAbove = [...sectionPathAbove, rowCount - 1];
            if (Editor.hasPath(editor, rowPathAbove)) {
                const cellCount = countNodesAtPath(editor, rowPathAbove);
                Transforms.select(editor, [...rowPathAbove, cellCount - 1]);
            }
            return;
        }
    }

    // We failed to find a cell, row, or section to move to. We now move
    // out of the table to the previous element in the editor, if we can...
    const [, tablePath] = table;
    if (Path.hasPrevious(tablePath)) {
        const pathAboveTable = Path.previous(tablePath);
        if (Editor.hasPath(editor, pathAboveTable)) {
            Transforms.select(editor, Editor.end(editor, pathAboveTable));
        }
    }
};

const countNodesAtPath = (editor: Editor, path: Path) => {
    let count = 0;
    for (const _ of Node.children(editor, path)) {
        count++;
    }
    return count;
};
